import { Box, Button, TextField } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import ErrorWarningIcon from "components/common/InputErrorWarningIcon";
import {
  inviteAdminUserSchema,
  InviteAdminUserFormData,
} from "validations/adminSchemas";
import { EPath } from "constants/path";

interface InviteAdminUserFormProps {
  onSubmit: (data: InviteAdminUserFormData) => void;
  isLoading?: boolean;
}

function InviteAdminUserForm({
  onSubmit,
  isLoading = false,
}: InviteAdminUserFormProps) {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<InviteAdminUserFormData>({
    resolver: yupResolver(inviteAdminUserSchema),
    mode: "onChange",
  });

  const getError = (
    fieldName: keyof InviteAdminUserFormData,
  ): { message?: string } | undefined => {
    return errors[fieldName] as { message?: string } | undefined;
  };

  const errorIcon = (fieldName: keyof InviteAdminUserFormData) => {
    const error = getError(fieldName);
    const isError = !!error;
    return {
      endAdornment: <ErrorWarningIcon hasError={isError} />,
    };
  };

  const handleCancel = () => {
    navigate(EPath.PRACTICE_MANAGEMENT);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField
            label="Account Email"
            variant="outlined"
            className="w-full"
            type="email"
            error={!!getError("email")}
            helperText={getError("email")?.message}
            slotProps={{
              input: errorIcon("email"),
              htmlInput: {
                maxLength: 255,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
            {...field}
          />
        )}
      />

      <Controller
        name="firstName"
        control={control}
        render={({ field }) => (
          <TextField
            label="First Name"
            variant="outlined"
            className="w-full"
            error={!!getError("firstName")}
            helperText={getError("firstName")?.message}
            slotProps={{
              input: errorIcon("firstName"),
              htmlInput: {
                maxLength: 64,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
            {...field}
          />
        )}
      />

      <Controller
        name="middleName"
        control={control}
        render={({ field }) => (
          <TextField
            label="Middle Name"
            variant="outlined"
            className="w-full"
            error={!!getError("middleName")}
            helperText={getError("middleName")?.message}
            slotProps={{
              input: errorIcon("middleName"),
              htmlInput: {
                maxLength: 64,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
            {...field}
          />
        )}
      />

      <Controller
        name="lastName"
        control={control}
        render={({ field }) => (
          <TextField
            label="Last Name"
            variant="outlined"
            className="w-full"
            error={!!getError("lastName")}
            helperText={getError("lastName")?.message}
            slotProps={{
              input: errorIcon("lastName"),
              htmlInput: {
                maxLength: 64,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
            {...field}
          />
        )}
      />

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={isLoading}
          sx={{
            minWidth: 120,
            height: 40,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!isValid || isLoading}
          sx={{
            minWidth: 120,
            height: 40,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {isLoading ? "Inviting..." : "Invite"}
        </Button>
      </Box>
    </Box>
  );
}

export default InviteAdminUserForm;
